import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  FormControl,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { useTranslation } from 'react-i18next';
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { PasswordTextField } from "./PasswordTextField";
import { useAuth } from "../../hooks/useAuth";

// Типы для состояния компонента
interface ResetState {
  password: string;
  passwordVerify: string;
}

const ResetForm: React.FC = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<ResetState>({
    password: "",
    passwordVerify: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordVerify, setShowPasswordVerify] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const auth = useAuth();

  const handleClickShowPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    setShowPassword((show) => !show);
    event.currentTarget.blur();
  }

  const handleClickShowPasswordVerify = (event: React.MouseEvent<HTMLButtonElement>) => {
    setShowPasswordVerify((show) => !show);
    event.currentTarget.blur();
  }

  // Предотвращаем дефолтное поведение при нажатии на иконку
  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  // Обработчик изменения полей
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value,
    }));
  };

  // Обработчик отправки формы
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrorMessage("");
    
    if (formData.password !== formData.passwordVerify) {
      setErrorMessage(t("SignupForm.PasswordsDoNotMatch"));
      return;
    }    

    try {
      // Метод берет токен из URL автоматически (из письма)
      await auth.resetPassword(auth.userName || "", );
      console.log("Пароль успешно обновлен!");
    } catch (err: any) {
      console.log(err.message || "Произошла ошибка");
      setErrorMessage(err.message || "Произошла ошибка");
    }  
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2, // Отступ между элементами (1 = 8px)
        maxWidth: 400,
        mx: "auto", // Центрирование формы
        p: 3,
        border: "1px solid #ccc",
        borderRadius: "8px",
      }}
    >
      <Typography variant="h4" component="h1" align="center">
        {t('ResetForm.Reset')}
      </Typography>
      
      {errorMessage && (
        <Typography color="error" align="center" sx={{ mt: -1, mb: 1, fontWeight: "bold" }}>
          {errorMessage}
        </Typography>
      )}      
      
      <FormControl fullWidth>
        <PasswordTextField
          label={t('SignupForm.Password')}
          name="password"
          // Тип поля меняется в зависимости от showPassword
          type={showPassword ? "text" : "password"}
          value={formData.password}
          onChange={handleInputChange}
          required
          variant="outlined"
          // Добавляем иконку
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </FormControl>

      <FormControl fullWidth>
        <PasswordTextField
          // label="Verify password"
          label={t('SignupForm.VerifyPassword')}
          name="passwordVerify"
          // Тип поля меняется в зависимости от showPasswordVerify
          type={showPasswordVerify ? "text" : "password"}
          value={formData.passwordVerify}
          onChange={handleInputChange}
          required
          variant="outlined"
          // Добавляем иконку
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={handleClickShowPasswordVerify}
                  onMouseDown={handleMouseDownPassword}
                  edge="end"
                >
                  {showPasswordVerify ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </FormControl>

      <Button type="submit" variant="contained">
        {t('ResetForm.set')}
      </Button>
    </Box>
  );
};

export default ResetForm;